// src/App.tsx
import { useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import type { CSSProperties } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { SortingState } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { HospitalResultsTable } from "./hospitalResults/HospitalResultsTable";
import { useHospitalCounters, useHospitalResults } from "./hospitalResults/hooks/useHospitalResults";
import type { HospitalResultRow } from "./hospitalResults/types";

/*
  БАЗА (как в рабочем эталоне):
  - сортировка (подсветка #48D1CC), шапка до 3 строк
  - ресайз колонок пикселями, sticky-header
  - виртуализация 100к строк
  - быстрые даты + произвольный период
  - полосы: горизонтальные и вертикальные (как раньше)
  - ID услуги/Диагноз — по контексту
  - !!! «Стоимость» вместо старых «з/О» и «з/Д»
*/

type ContextKind = "Амбулатория" | "Стационар" | "Скорая помощь";

type RowServer = {
  id: string;
  docId: string | null;   // <<< ДОБАВЛЕНО

  referralDateTime: string; // "YYYY-MM-DD HH:mm"
  serviceDateTime: string;  // "YYYY-MM-DD HH:mm"
  iin: string;

  serviceId: string | null; // == MisID (для Амбулатории)
  serviceCode: string | null;
  serviceName: string | null;
  mkb10: string | null;     // для Стационара/Скорой

  department: string | null;
  doctor: string | null;

  errorHint: string | null;
  status: "исправлено" | "не исправлено" | "в работе";

  cost: number | null;      // ЕДИНЫЙ столбец «Стоимость»
  context: ContextKind;

  orgId: string | null;
  orgName: string | null;

  // повод обращения (legacy-код МИС + название), место оказания, тип подразделения
  treatmentReasonCode: string | null;
  treatmentReasonName: string | null;
  placeOfService: string | null;
  deptType: string | null;
  ruleCode: string | null;
};

type Row = RowServer & {
  referralTs: number;
  serviceTs: number;
  doctorLc: string;
  serviceNameLc: string;
};

type OrgDictItem = {
  orgId: string | null;
  orgName: string | null;
  totalServices: number;
};

type DepartmentDictItem = {
  department: string | null;
  totalServices: number;
};

type DoctorDictItem = {
  doctor: string | null;
  totalServices: number;
};

type TreatmentReasonDictItem = { code: string; name: string };

const DEPT_TYPE_OPTIONS = ["ПМСП", "ВА", "ФАП", "МП", "Прочие"] as const;

const PLACE_OF_SERVICE_LABELS: Record<string, string> = {
  ВПоликлинике: "в поликлинике",
  НаДому: "на дому",
  ВШколе: "в школе",
  ПередвижнойМедКомплекс: "передвижной медкомплекс",
  Дистанционное: "дистанционно",
  Травмпункт: "травмпункт",
};

type Role = "SELF" | "DEPARTMENT" | "ORG" | "DIRECTOR" | "SUPERADMIN";

const APP_DASHBOARD_TEST_HREF = "/dashboards/app/test";

const ACCENT = "#00B8BA";

const pagerBtn: CSSProperties = {
  border: "1px solid #cbd5e1",
  background: "#fff",
  borderRadius: 6,
  padding: "2px 8px",
  fontSize: 12,
  cursor: "pointer",
  color: "#0f172a",
};
const SORT_HL = "#48D1CC";

/* ========================= helpers ========================= */

function parseYmdHm(s: string): number {
  const [d, t = "00:00"] = s.split(" ");
  const [Y, M, D] = (d || "").split("-").map((x) => (x === "" ? NaN : Number(x)));
  const [h, m] = (t || "00:00").split(":").map((x) => (x === "" ? NaN : Number(x)));
  const year = Number.isFinite(Y) ? Y : new Date().getFullYear();
  const month = Number.isFinite(M) ? M - 1 : 0;
  const day = Number.isFinite(D) ? D : 1;
  const hh = Number.isFinite(h) ? h : 0;
  const mm = Number.isFinite(m) ? m : 0;
  return new Date(year, month, day, hh, mm, 0, 0).getTime();
}

function toViewRow(r: RowServer): Row {
  const referralTs = parseYmdHm(r.referralDateTime || "");
  const rawServiceTs = parseYmdHm(r.serviceDateTime || "");
  const serviceTs = Number.isFinite(rawServiceTs) ? rawServiceTs : referralTs;
  return {
    ...r,
    referralTs,
    serviceTs,
    doctorLc: (r.doctor || "").toLowerCase(),
    serviceNameLc: (r.serviceName || "").toLowerCase(),
  };
}

function tsToIsoUTC(ts: number): string {
  return new Date(ts).toISOString();
}
function formatMoneySpaces(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  const n = typeof val === "string" ? Number(val) : val;
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// ISO -> "YYYY-MM-DD HH:mm"
function isoToYmdHm(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const Y = d.getFullYear();
  const M = pad(d.getMonth() + 1);
  const D = pad(d.getDate());
  const h = pad(d.getHours());
  const m = pad(d.getMinutes());
  return `${Y}-${M}-${D} ${h}:${m}`;
}

function asNumberOrNull(v: any): number | null {
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? Number(n) : null;
}

function makeInitialColW(cols: number): number[] {
  const base = [260,240,160,200,160,220,180,320,220,104,96];
  if (cols === base.length) return [...base];
  const w = Math.max(80, Math.floor(1400 / cols));
  return Array.from({ length: cols }, () => w);
}

/* ========================= Auth ========================= */

type AuthPayload = {
  access_token: string;
  token_type: "bearer";
  iin: string;
  login: string;
  fio?: string;
};

type User = { name: string; iin: string };

function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [auth, setAuth] = useState<AuthPayload | null>(null);

  useEffect(() => {
    try {
      const a = localStorage.getItem("auth_payload");
      if (a) {
        const parsed: AuthPayload = JSON.parse(a);
        setAuth(parsed);
        setUser({ name: parsed.fio || parsed.iin, iin: parsed.iin });
      }
    } catch {}
  }, []);

  async function loginBackend(login: string, password: string) {
    const url = buildUrl("/api/v1/auth/login");
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ login, password }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || "Неверный ИИН или пароль");
    }
    const payload: AuthPayload = await res.json();
    localStorage.setItem("auth_payload", JSON.stringify(payload));
    setAuth(payload);
    setUser({ name: payload.fio || payload.iin, iin: payload.iin });
  }

  function logout() {
    localStorage.removeItem("auth_payload");
    setAuth(null);
    setUser(null);
  }

  return { user, auth, loginBackend, logout } as const;
}

function buildUrl(path: string) {
  let base = String((import.meta as any).env?.VITE_API_BASE || "").replace(/\/+$/, "");
  let p = path.startsWith("/") ? path : `/${path}`;
  if (/\/api$/i.test(base) && /^\/api\//i.test(p)) p = p.replace(/^\/api/i, "");
  return `${base}${p}`;
}

async function apiFetch(
  path: string,
  opts: RequestInit = {},
  auth?: AuthPayload | null
) {
  const url = buildUrl(path);
  const headers: Record<string, string> = { ...(opts.headers as any) };
  if (auth?.access_token) headers["Authorization"] = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const res = await fetch(url, { ...opts, headers, credentials: "omit" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

/* ========================= Роль ========================= */

function useRole(auth: AuthPayload | null) {
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!auth?.access_token || !auth?.iin) return;
      setLoading(true); setErr(null);
      try {
        const data = await apiFetch("/api/v1/analytics/role", {}, auth);
        if (!cancelled) setRole((data?.role as Role) || "SELF");
      } catch (e: any) {
        const msg = String(e?.message || "");
        if (!cancelled) {
          if (msg.startsWith("API 401") || msg.startsWith("API 403")) {
            setRole("SELF");
            setErr(null);
          } else {
            setErr(msg || "Ошибка роли");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [auth?.access_token, auth?.iin]);

  return { role, loading, err } as const;
}

/* ========================= Фильтры ========================= */

type QuickRange = "" | "сегодня" | "вчера" | "неделя" | "месяц" | "год";
type Filters = {
  context?: ContextKind;
  department?: string;
  doctor?: string;
  serviceName?: string;
  quick?: QuickRange;
  from?: string; // "YYYY-MM-DD"
  to?: string;   // "YYYY-MM-DD"
  orgId?: string;
  deptType?: string;
  treatmentReason?: string;
};

function useDebounced<T>(value: T, ms = 200) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
function quickRangeToTs(q: QuickRange, now = new Date()): [number, number] | null {
  if (!q) return null;
  const end = now.getTime();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  switch (q) {
    case "сегодня":
      return [start.getTime(), end];
    case "вчера": {
      const s = new Date(start); s.setDate(s.getDate() - 1);
      const e = new Date(start); e.setMilliseconds(-1);
      return [s.getTime(), e.getTime()];
    }
    case "неделя": {
      const day = start.getDay();
      const diffToMon = (day + 6) % 7;
      start.setDate(start.getDate() - diffToMon);
      return [start.getTime(), end];
    }
    case "месяц":
      start.setDate(1);
      return [start.getTime(), end];
  }
  start.setMonth(0, 1);
  return [start.getTime(), end];
}

/**
 * Окно дат для всех запросов экрана (таблица, счётчики, справочники).
 * Раньше этот расчёт был скопирован в каждый из трёх эффектов — три панели
 * могли разъехаться по периоду. Теперь источник один.
 */
function resolveAnalyticsWindow(filters: Filters, now = new Date()): [number, number] {
  if (filters.from || filters.to) {
    const startTs = filters.from ? new Date(filters.from + "T00:00:00").getTime() : 0;
    const endTs = filters.to ? new Date(filters.to + "T23:59:59.999").getTime() : now.getTime();
    return [startTs, endTs];
  }
  const r = quickRangeToTs(filters.quick || "сегодня", now);
  return r ? r : [0, now.getTime()];
}

function tsToYmd(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function resolveHospitalWindow(filters: Filters): { from_date: string; to_date: string } {
  const now = new Date();
  if (filters.from || filters.to) {
    const fromTs = filters.from ? new Date(`${filters.from}T00:00:00`).getTime() : now.getTime();
    const toTs = filters.to ? new Date(`${filters.to}T23:59:59.999`).getTime() : now.getTime();
    return {
      from_date: tsToYmd(fromTs),
      to_date: tsToYmd(toTs),
    };
  }

  const fromTo = quickRangeToTs(filters.quick || "сегодня", now);
  const fromTs = fromTo?.[0] ?? now.getTime();
  const toTs = fromTo?.[1] ?? now.getTime();
  return {
    from_date: tsToYmd(fromTs),
    to_date: tsToYmd(toTs),
  };
}

/* ========================= Стили ========================= */

const css = {
  page: ({
    fontFamily: "Inter, system-ui, Arial, sans-serif",
    background: "#f7f8fa",
    minHeight: "100vh",
    color: "#0f172a",
    margin: 0,
  } as CSSProperties),
  container: ({
    maxWidth: "100%",
    width: "100%",
    margin: "0 auto",
    padding: "16px 24px",
    boxSizing: "border-box",
  } as CSSProperties),
  topbar: ({
    position: "sticky",
    top: 0,
    zIndex: 10,
    borderBottom: "1px solid #e5e7eb",
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(6px)",
  } as CSSProperties),
  topbarInner: { maxWidth: "100%", margin: "0 auto", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  tag: { background: "#eef2ff", color: "#3730a3", padding: "2px 8px", borderRadius: 999, fontSize: 12, marginLeft: 8 },
  headerTitle: { fontWeight: 600 } as CSSProperties,

  gridMain: { display: "grid", gridTemplateColumns: "minmax(115px, 130px) 1fr", gap: 4, alignItems: "start" },

  card: ({
    background: ACCENT,
    border: "1px solid " + ACCENT,
    borderRadius: 12,
    padding: 12,
    color: "#fff",
    minHeight: 60,
    width: "100%",
    margin: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 6,
    boxSizing: "border-box",
  } as CSSProperties),
  cardTitle: { fontSize: 14, fontWeight: 600, marginBottom: 8, color: "#fff" },
  bigNum: { fontSize: 24, fontWeight: 700, color: "#fff" },

  filtersWrap: { display: "grid", gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 8 },
  input: ({
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    boxSizing: "border-box",
    background: "#fff",
  } as CSSProperties),
  button: { padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer" },

  topLinkButton: {
  padding: "6px 10px",
  borderRadius: 4,
  border: "1px solid #48B1CC",
  background: "#48B1CC",
  color: "#fff",
  cursor: "pointer",
  textDecoration: "none",
  },

  subLine: ({
    display: "inline-flex",
    alignItems: "baseline",
    gap: 6,
    whiteSpace: "nowrap",
  } as CSSProperties),
  subNum: ({
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums lining-nums",
  } as CSSProperties),
  currency: ({
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.1,
    opacity: 0.95,
  } as CSSProperties),
};

/* ========================= UI ========================= */

function TopBar({
  user,
  showMenu,
  onLogout,
}: {
  user: User;
  showMenu: boolean;
  onLogout: () => void;
}) {
  return (
    <div style={css.topbar}>
      <div style={css.topbarInner}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={css.headerTitle}>Defektolog</span>
          <span style={css.tag}>frontend</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          {showMenu ? (
            <button
              type="button"
              onClick={() => window.open(APP_DASHBOARD_TEST_HREF, "_blank", "noopener,noreferrer")}
              style={{
                ...css.topLinkButton,
                minWidth: 120,
              }}
            >
              Dashboards
            </button>
          ) : null}

          <span>{user.name}</span>
          <button style={css.button as any} onClick={onLogout}>Выйти</button>
        </div>
      </div>
    </div>
  );
}


function CompactFilters({
  filters,
  setFilters,
  role,
  onExport,
  canExport,
  orgOptions,
  departmentOptions,
  doctorOptions,
  treatmentReasonOptions,
}: {
  filters: Filters;
  setFilters: (f: Filters) => void;
  role: Role;
  onExport?: () => void;
  canExport?: boolean;
  orgOptions: { id: string; label: string }[];
  departmentOptions: string[];
  doctorOptions: string[];
  treatmentReasonOptions: TreatmentReasonDictItem[];
}) {
  const quickOptions: QuickRange[] = ["", "сегодня", "вчера", "неделя", "месяц", "год"];
  const isSelf = role === "SELF";
  const isSuper = role === "SUPERADMIN";

  // Тип подразделения и повод обращения — только для амбулатории: в стационаре
  // своя таблица и свои фильтры.
  const showAppFilters = filters.context !== "Стационар";
  const colsCount = (isSuper ? 7 : 6) + (showAppFilters ? 2 : 0);

  return (
    <div
      style={{
        ...css.filtersWrap,
        gridTemplateColumns: `repeat(${colsCount}, minmax(0,1fr))`,
      }}
    >
      {/* 0. Организация (только для SUPERADMIN) */}
      {isSuper && (
        <select
          value={filters.orgId ?? ""}
          onChange={(e) => setFilters({ ...filters, orgId: e.target.value || undefined })}
          style={css.input as any}
        >
          <option value="">Организация (все)</option>
          {orgOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      )}

      {/* 1. Контекст */}
      <select
        value={filters.context ?? ""}
        onChange={(e) =>
          setFilters({ ...filters, context: (e.target.value as ContextKind) || undefined })
        }
        style={css.input as any}
      >
        <option value="">Место оказания (все)</option>
        <option value="Амбулатория">Амбулатория</option>
        <option value="Стационар">Стационар (включая ДС и ЦРБ)</option>
        <option value="Скорая помощь">Скорая помощь</option>
      </select>

      {/* 2а. Тип подразделения */}
      {showAppFilters && (
        <select
          value={filters.deptType ?? ""}
          onChange={(e) =>
            // смена типа сбрасывает отделение и врача: они могли быть из другого типа
            setFilters({ ...filters, deptType: e.target.value || undefined, department: undefined, doctor: undefined })
          }
          style={css.input as any}
          title="Тип подразделения: ПМСП, врачебная амбулатория, ФАП, медпункт"
        >
          <option value="">Тип подразделения (все)</option>
          {DEPT_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}

      {/* 2. Отделение */}
      <select
        value={filters.department ?? ""}
        onChange={(e) => setFilters({ ...filters, department: e.target.value || undefined, doctor: undefined })}
        style={css.input as any}
      >
        <option value="">Отделение (все)</option>
        {departmentOptions.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>

      {/* 3. Для SELF — поиск по услуге, иначе — селект «Врач» */}
      {isSelf ? (
        <input
          placeholder="Наименование услуги (поиск)"
          value={filters.serviceName ?? ""}
          onChange={(e) => setFilters({ ...filters, serviceName: e.target.value || undefined })}
          style={css.input as any}
        />
      ) : (
        <select
          value={filters.doctor ?? ""}
          onChange={(e) => setFilters({ ...filters, doctor: e.target.value || undefined })}
          style={css.input as any}
        >
          <option value="">Врач (все)</option>
          {doctorOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      )}

      {/* 3а. Повод обращения */}
      {showAppFilters && (
        <select
          value={filters.treatmentReason ?? ""}
          onChange={(e) => setFilters({ ...filters, treatmentReason: e.target.value || undefined })}
          style={css.input as any}
        >
          <option value="">Повод обращения (все)</option>
          {treatmentReasonOptions.map((t) => (
            <option key={t.code} value={t.code}>
              {t.name}
            </option>
          ))}
        </select>
      )}

      {/* 4. Быстрый период */}
      <select
        value={filters.quick ?? ""}
        onChange={(e) =>
          setFilters({
            ...filters,
            quick: e.target.value as QuickRange,
            from: undefined,
            to: undefined,
          })
        }
        style={css.input as any}
      >
        <option value="">Весь период</option>
        {quickOptions
          .filter(Boolean)
          .map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
      </select>

      {/* 5. Дата "с" */}
      <input
        type="date"
        value={filters.from ?? ""}
        onChange={(e) => setFilters({ ...filters, from: e.target.value || undefined })}
        style={css.input as any}
      />

      {/* 6. Дата "по" */}
      <input
        type="date"
        value={filters.to ?? ""}
        onChange={(e) => setFilters({ ...filters, to: e.target.value || undefined })}
        style={css.input as any}
      />

      {/* Кнопки */}
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
        }}
      >
        <button
          style={css.button as any}
          onClick={() => setFilters({ quick: "сегодня" })}
        >
          Сбросить
        </button>
        {canExport && (
          <button
            style={
              {
                ...css.button,
                background: ACCENT,
                color: "#fff",
                borderColor: ACCENT,
                fontWeight: 700,
              } as any
            }
            onClick={onExport}
          >
            Выгрузить
          </button>
        )}
      </div>
    </div>
  );
}

/* ========================= Counters ========================= */

type Counters = {
  services: { totalServices: number };
  events: {
    okFailEvents: number;
    sumCostFailEvents: string;
    okTrueEvents: number;
    sumCostTrueEvents: string;
    fixTransitions: number;
    sumCostFixedNow: string;
  };
  now: {
    totalDocs: number;
    totalErrorsNow: number;
    sumCostErrorsNow: string;
    specialistsWithErrorsNow: number;
  };
};

function LeftPanel({ rows, counters, loading }: { rows: Row[]; counters: Counters | null; loading: boolean }) {
  const fallback = {
    totalServices: rows.length,
    totalErrorsNow: rows.filter((r) => r.status !== "исправлено").length,
    specialistsWithErrorsNow: new Set(rows.filter((r) => r.status !== "исправлено").map((r) => r.doctor || "")).size,
    fixedTransitions: rows.filter((r) => r.status === "исправлено").length,
    sumCostErrorsNow: null as number | null,
    sumCostFixedNow: null as number | null,
  };

  const useDB = !!counters && !loading;

  const Card = ({ title, value, sub }: { title: string; value: string; sub?: string }) => (
    <div style={css.card}>
      <div style={css.cardTitle}>{title}</div>
      <div style={css.bigNum}>{value}</div>
      {sub !== undefined && (
        <div style={css.subLine as any}>
          <span style={css.subNum as any}>{sub}</span>
          <span style={css.currency as any}>₸</span>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 8, overflow: "hidden" }}>
      <Card
        title="Счётчик услуг"
        value={(useDB ? counters!.services.totalServices : fallback.totalServices).toLocaleString("ru-RU")}
      />
      <Card
        title="Счётчик ошибок"
        value={(useDB ? counters!.now.totalErrorsNow : fallback.totalErrorsNow).toLocaleString("ru-RU")}
        sub={formatMoneySpaces(useDB ? counters!.now.sumCostErrorsNow : fallback.sumCostErrorsNow)}
      />
      <Card
        title="Специалистов с ошибками"
        value={(useDB ? counters!.now.specialistsWithErrorsNow : fallback.specialistsWithErrorsNow).toLocaleString("ru-RU")}
      />
      <Card
        title="Исправленные ошибки"
        value={(useDB ? counters!.events.fixTransitions : fallback.fixedTransitions).toLocaleString("ru-RU")}
        sub={formatMoneySpaces(useDB ? counters!.events.sumCostFixedNow : fallback.sumCostFixedNow)}
      />
    </div>
  );
}

/* ========================= Export CSV ========================= */

function exportToCsv(rows: Row[], role: Role) {
  const directorCols = [
    ["referralDateTime", "дата направления / дата время госпитализации"],
    ["serviceDateTime",  "дата оказания / дата время выписки"],
    ["deptType",         "тип подразделения"],
    ["department",       "отделение"],
    ["doctor",           "врач"],
    ["treatmentReasonName", "повод обращения"],
    ["serviceCode",      "код услуги приёма"],
    ["serviceName",      "услуга приёма"],
    ["errorHint",        "подсказка"],
    ["status",           "статус"],
    ["cost",             "стоимость"],
  ] as const;

  const fullCols = [
    ["referralDateTime", "дата направления / дата время госпитализации"],
    ["serviceDateTime",  "дата оказания / дата время выписки"],
    ["deptType",         "тип подразделения"],
    ["department",       "отделение"],
    ["doctor",           "врач"],
    ["iin",              "ИИН пациента"],
    ["treatmentReasonName", "повод обращения"],
    ["serviceId",        "ID услуги / диагноз"],
    ["serviceCode",      "код услуги"],
    ["serviceName",      "наименование услуги"],
    ["errorHint",        "подсказка"],
    ["status",           "статус"],
    ["cost",             "стоимость"],
    ["context",          "контекст"],
  ] as const;

  const cols = role === "DIRECTOR" ? directorCols : fullCols;

  const esc = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",;\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const header = cols.map(([, title]) => esc(title)).join(";");
  const lines = rows.map(r =>
    cols.map(([k]) => {
      const val = (r as any)[k];
      if (k === "cost" && val != null && Number.isFinite(val)) return String(val);
      return esc(val ?? "");
    }).join(";")
  );

  // BOM для корректного UTF-8 в Excel
  const csv = "\uFEFF" + [header, ...lines].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,"-");
  a.download = `analytics_${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function exportHospitalToCsv(rows: HospitalResultRow[]) {
  const cols = [
    ["discharge_date", "дата выписки"],
    ["department", "отделение"],
    ["doctor", "врач"],
    ["patient_iin", "ИИН пациента"],
    ["diagnosis", "диагноз"],
    ["hospital_type", "тип стационара"],
    ["defect_codes", "код дефекта"],
    ["defect_hints", "подсказка"],
  ] as const;

  const esc = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",;\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const header = cols.map(([, title]) => esc(title)).join(";");
  const lines = rows.map((row) =>
    cols
      .map(([key]) => {
        let val: unknown;
        switch (key) {
          case "discharge_date":
            val = row.discharge_date ? new Date(row.discharge_date).toISOString() : "";
            break;
          case "department":
            val = row.department ?? "";
            break;
          case "doctor":
            val = row.doctor ?? "";
            break;
          case "patient_iin":
            val = row.patient_iin ?? "";
            break;
          case "diagnosis":
            val = row.diagnosis ?? "";
            break;
          case "hospital_type":
            val = row.hospital_type ?? "";
            break;
          case "defect_codes":
            val = (row.defect_codes || []).join(" | ");
            break;
          case "defect_hints":
            val = (row.defect_hints || []).join(" | ");
            break;
          default:
            val = "";
        }
        return esc(val);
      })
      .join(";"),
  );

  const csv = "\uFEFF" + [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  a.download = `hospital_analytics_${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/* ========================= Карточка дефекта ========================= */

function DefectCard({
  row,
  showIin,
  onClose,
  onOpenMisDoc,
}: {
  row: Row;
  showIin: boolean;
  onClose: () => void;
  onOpenMisDoc?: (row: Row) => void;
}) {
  const money = (v: number | null) =>
    v == null ? "—" : new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  const place = row.placeOfService ? PLACE_OF_SERVICE_LABELS[row.placeOfService] ?? row.placeOfService : null;
  const facts: [string, React.ReactNode][] = [
    ["Дата поступления на проверку", row.referralDateTime || "—"],
    ["Организация", row.orgName || row.orgId || "—"],
    ["Подразделение", [row.department, row.deptType ? `(${row.deptType})` : ""].filter(Boolean).join(" ") || "—"],
    ["Врач", row.doctor || "—"],
    ["Повод обращения", row.treatmentReasonName || "—"],
    ["Место оказания", place || "—"],
    [
      "Услуга приёма",
      row.serviceCode
        ? `${row.serviceCode} — ${row.serviceName || "названия нет в тарификаторе"}`
        : "—",
    ],
    ["Диагноз (МКБ-10)", row.mkb10 || "—"],
    ["Код дефекта", row.ruleCode || "—"],
    ["Стоимость", money(row.cost)],
    ["Статус", row.status],
  ];
  if (showIin) facts.splice(4, 0, ["ИИН пациента", row.iin || "—"]);
  const canOpen = !!(onOpenMisDoc && row.docId && row.orgId);

  return (
    <div className="hint-overlay" onClick={onClose}>
      <div className="hint-card" onClick={(e) => e.stopPropagation()}>
        <div className="hint-title">Дефект</div>
        <div className="hint-body" style={{ marginBottom: 12 }}>{row.errorHint || "—"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 220px) 1fr", gap: "6px 12px", fontSize: 13 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: "contents" }}>
              <div style={{ color: "#64748b" }}>{k}</div>
              <div>{v}</div>
            </div>
          ))}
        </div>
        <div className="hint-actions" style={{ gap: 8 }}>
          {canOpen && (
            <button
              onClick={() => onOpenMisDoc?.(row)}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid " + ACCENT, background: ACCENT, color: "#fff", cursor: "pointer" }}
            >
              Открыть документ в МИС ↗
            </button>
          )}
          <button
            onClick={onClose}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer" }}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================= Data grid ========================= */

const ROW_HEIGHT = 56;

function DataGrid({
  data,
  role,
  onOpenMisDoc,
}: {
  data: Row[];
  role: Role;
  onOpenMisDoc?: (row: Row) => void;
}) {
  const columnHelper = createColumnHelper<Row>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const parentRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  const [scrollPad, setScrollPad] = useState(0);

  // Карточка дефекта: полная подсказка + все факты строки (повод, услуга, врач,
  // подразделение, место оказания) и переход в документ МИС.
  const [detailRow, setDetailRow] = useState<Row | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDetailRow(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isDirector = role === "DIRECTOR";

  const colsDirector = [
    columnHelper.accessor("referralDateTime", { header: "дата направления / дата время госпитализации" }),
    columnHelper.accessor("serviceDateTime",  { header: "дата оказания / дата время выписки" }),
    columnHelper.accessor("department",       { header: "отделение" }),
    columnHelper.accessor("doctor",           { header: "врач" }),
    columnHelper.accessor((r) => r.treatmentReasonName ?? "", { id: "treatmentReason", header: "повод обращения" }),
    columnHelper.accessor((r) => r.serviceCode ?? "", {
      id: "serviceVisit",
      header: "услуга приёма",
      cell: ({ row }) => {
        const r = row.original;
        if (!r.serviceCode) return "";
        return (
          <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.35 }}>
            <b>{r.serviceCode}</b>{r.serviceName ? ` ${r.serviceName}` : ""}
          </span>
        );
      },
    }),
    columnHelper.accessor("errorHint", {
      header: "подсказка",
      cell: (ctx) => {
        const txt = ctx.getValue() || "";
        return (
          <button
            onClick={(e) => { e.stopPropagation(); setDetailRow(ctx.row.original); }}
            style={{
              all: "unset",
              cursor: "pointer",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.35,
            }}
            title="Открыть карточку дефекта"
          >
            {String(txt)}
          </button>
        );
      },
    }),
    columnHelper.accessor((r) => r.cost ?? null, {
      id: "cost",
      header: "стоимость",
      cell: ({ getValue }) => {
        const v = getValue();
        return v == null
          ? "—"
          : new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
      },
    }),
  ];

  const colsFull = [
    columnHelper.accessor("referralDateTime", { header: "дата направления / дата время госпитализации" }),
    columnHelper.accessor("serviceDateTime",  { header: "дата оказания / дата время выписки" }),
    columnHelper.accessor("department",       { header: "отделение" }),
    columnHelper.accessor("doctor",           { header: "врач" }),
    columnHelper.accessor("iin",              { header: "ИИН пациента" }),
    columnHelper.accessor((r) => r.treatmentReasonName ?? "", { id: "treatmentReason", header: "повод обращения" }),
    columnHelper.display({
      id: "serviceId_diag",
      header: "ID услуги / диагноз",
      cell: ({ row }) => {
        const r = row.original;
        const label =
          r.context === "Амбулатория"
            ? (r.serviceId ?? "")
            : (r.mkb10 ?? "");
        if (!label) return "";

        const clickable = !!(onOpenMisDoc && r.docId && r.orgId);

        if (!clickable) {
          return label;
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenMisDoc?.(r);
            }}
            style={{
              all: "unset",
              cursor: "pointer",
              color: ACCENT,
              textDecoration: "underline",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            title="Открыть документ в МИС"
          >
            <span>{label}</span>
            <span style={{ fontSize: 12 }}>↗</span>
          </button>
        );
      },
      sortingFn: (a, b): number => {
        const va = (a.original.context === "Амбулатория" ? a.original.serviceId : a.original.mkb10) || "";
        const vb = (b.original.context === "Амбулатория" ? b.original.serviceId : b.original.mkb10) || "";
        return va.localeCompare(vb, "ru");
      },
    }),
    columnHelper.accessor((r) => r.serviceCode ?? "", { id: "serviceCodeOnly", header: "код услуги" }),
    columnHelper.accessor((r) => r.serviceName ?? "", { id: "serviceNameOnly", header: "наименование услуги" }),
    columnHelper.accessor("errorHint", {
      header: "подсказка",
      cell: (ctx) => {
        const txt = ctx.getValue() || "";
        return (
          <button
            onClick={(e) => { e.stopPropagation(); setDetailRow(ctx.row.original); }}
            style={{
              all: "unset",
              cursor: "pointer",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.35,
            }}
            title="Открыть карточку дефекта"
          >
            {String(txt)}
          </button>
        );
      },
    }),
    columnHelper.accessor((r) => r.cost ?? null, {
      id: "cost",
      header: "стоимость",
      cell: ({ getValue }) => {
        const v = getValue();
        return v == null
          ? "—"
          : new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
      },
    }),
  ];

  const columns = useMemo(() => (isDirector ? colsDirector : colsFull), [isDirector]);

  const [colW, setColW] = useState<number[]>(makeInitialColW(columns.length));
  const MIN = { flex: 80, status: 13 * 8, price: 72 };

  useEffect(() => {
    setColW(prev => (prev.length === columns.length ? prev : makeInitialColW(columns.length)));
  }, [columns.length]);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      const pad = el.offsetWidth - el.clientWidth;
      setScrollPad(pad);
      if (w <= 0) return;
      setContainerW(w);
      setColW(prev => {
        const sum = prev.reduce((a, b) => a + b, 0);
        if (sum === w) return prev;
        const k = w / sum;
        const fixedIdx = new Set([ (columns.length - 2), (columns.length - 1) ]);
        const next = prev.map((px, i) =>
          fixedIdx.has(i) ? px : Math.max(Math.round(px * k), MIN.flex)
        );
        const diff = w - next.reduce((a, b) => a + b, 0);
        next[0] += diff;
        return next;
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [columns.length]);

  const startResize = (i: number, e: React.MouseEvent) => {
    e.preventDefault();
    const j = i + 1;
    const startX = e.clientX;
    const init = [...colW];

    const minFor = (idx: number) => (idx === columns.length - 2 ? MIN.status : idx === columns.length - 1 ? MIN.price : MIN.flex);
    const minI = minFor(i);
    const minJ = minFor(j);

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      let wI = init[i] + dx;
      let wJ = init[j] - dx;
      if (wI < minI) { wJ -= minI - wI; wI = minI; }
      if (wJ < minJ) { wI -= minJ - wJ; wJ = minJ; }
      const next = [...init];
      next[i] = wI; next[j] = wJ;
      const sum = next.reduce((a, b) => a + b, 0);
      if (Math.abs(sum - containerW) <= 2) setColW(next);
      else { next[0] += containerW - sum; setColW(next); }
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const gridTemplate = useMemo(() => colW.map(px => `${px}px`).join(" "), [colW]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  return (
    <div className="wrap">
      <style>{`
        .wrap {
          /* 250, а не 220: под таблицей появилась строка постраничной навигации
             (22px + 6px отступа). Без этой поправки страница вылезала за экран. */
          height: calc(100vh - 250px);
          min-height: 520px;
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fff;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .thead {
          position: sticky;
          top: 0;
          z-index: 1;
          display: grid;
          grid-auto-flow: column;
          align-items: stretch;
          background: ${ACCENT};
          min-height: 84px;
          height: auto;
          border-bottom: 1px solid #e5e7eb;
          user-select: none;
        }
        .th {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          padding: 8px 10px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          color: ${"#fff"};
          border-right: 1px solid #f3f4f6;
          overflow: hidden;
          position: relative;
          cursor: pointer;
        }
        .th.sorted { background: ${SORT_HL}; color: #fff; }
        .th-label {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.35;
          white-space: normal;
        }
        .sort-mark { margin-left: auto; font-size: 12px; padding-top: 2px; }
        .resize { 
          position: absolute; right: 0; top: 0; width: 8px; height: 100%;
          cursor: col-resize; user-select: none; touch-action: none;
        }
        .tbody { flex: 1 1 auto; overflow: auto; }
        .row {
          position: absolute; left: 0; right: 0;
          display: grid; grid-auto-flow: column; align-items: stretch;
          height: ${ROW_HEIGHT}px;
          border-top: 2px solid #f3f4f6;
          background: #fff;
        }
        .cell {
          display: flex; align-items: center; padding: 0 10px;
          font-size: 13px; color: #111827;
          border-right: 1px solid #f3f4f6;
          white-space: normal; overflow: hidden;
        }
        .cell.center { justify-content: center; }

        .hint-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex; align-items: center; justify-content: center;
          z-index: 50;
        }
        .hint-card {
          max-width: 720px; width: min(92vw, 720px);
          max-height: 80vh; overflow: auto;
          background: #fff; border-radius: 12px; border: 1px solid #e5e7eb;
          padding: 16px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        .hint-title { font-weight: 700; margin-bottom: 8px; }
        .hint-body { white-space: pre-wrap; line-height: 1.45; }
        .hint-actions { display: flex; justify-content: flex-end; margin-top: 12px; }
      `}</style>

      {/* HEADER */}
      <div
        className="thead"
        style={{ gridTemplateColumns: gridTemplate, paddingRight: scrollPad }}
      >
        {table.getFlatHeaders().map((header, idx) => {
          const isSorted = !!header.column.getIsSorted();
          const isLast = idx === colW.length - 1;
          return (
            <div
              key={header.id}
              className={`th ${isSorted ? "sorted" : ""}`}
              onClick={(e) => {
                if ((e.target as HTMLElement).classList.contains("resize")) return;
                header.column.toggleSorting(undefined, e.shiftKey);
              }}
            >
              <span className="th-label">
                {flexRender(header.column.columnDef.header, header.getContext())}
              </span>
              <span className="sort-mark">
                {header.column.getIsSorted() === "asc" ? "▲" : header.column.getIsSorted() === "desc" ? "▼" : ""}
              </span>
              {!isLast && <div className="resize" onMouseDown={(ev) => startResize(idx, ev)} />}
            </div>
          );
        })}
      </div>

      {/* BODY */}
      <div ref={parentRef} className="tbody">
        <div style={{ height: rowVirtualizer.getTotalSize(), width: containerW, minWidth: "100%", position: "relative" }}>
          {rowVirtualizer.getVirtualItems().map((vr) => {
            const row = table.getRowModel().rows[vr.index];
            return (
              <div
                key={row.id}
                className="row"
                style={{ transform: `translateY(${vr.start}px)`, gridTemplateColumns: gridTemplate, cursor: "pointer" }}
                onClick={() => setDetailRow(row.original)}
                title="Открыть карточку дефекта"
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className="cell">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Карточка дефекта */}
      {detailRow && (
        <DefectCard
          row={detailRow}
          showIin={!isDirector}
          onClose={() => setDetailRow(null)}
          onOpenMisDoc={onOpenMisDoc}
        />
      )}
    </div>
  );
}

/* ========================= Dashboard ========================= */

/** Ответ /api/v1/analytics/table -> строки таблицы. Общее для показа и для выгрузки. */
function mapServerRows(raw: any): Row[] {
  const src: any[] = Array.isArray(raw) ? raw : (raw?.items || raw?.rows || raw?.data || []);

  const safe: RowServer[] = src.map((r: any) => {
    const status: RowServer["status"] =
      r.ok === true ? "исправлено" : r.ok === false ? "не исправлено" : "не исправлено";

    const department =
      r.fpname ??
      r.departmentName ??
      r.department ??
      r.performerDepartment ??
      r.deptName ??
      r.orgName ??
      r.fname ??
      null;

    const docId: string | null = r.docId ? String(r.docId) : null;

    return {
      id: String(r.id ?? docId ?? crypto.randomUUID()),
      docId,
      referralDateTime: isoToYmdHm(r.arrivedAt ?? r.referralDateTime ?? r.hospitalizedAt ?? null),
      serviceDateTime:  isoToYmdHm(r.serviceDateTime ?? r.dischargedAt ?? null),
      iin: String(r.iin ?? ""),
      serviceId: r.serviceId ?? null,
      serviceCode: r.serviceCode ?? null,
      serviceName: r.serviceName ?? null,
      mkb10: r.mkb10 ?? null,
      department,
      doctor: r.doctor ?? null,
      errorHint: r.errorHint ?? null,
      status,
      cost: asNumberOrNull(r.cost),
      context: (r.context || "Амбулатория") as ContextKind,
      orgId: r.orgId ?? null,
      orgName: r.orgName ?? null,
      treatmentReasonCode: r.treatmentReasonCode ?? null,
      treatmentReasonName:
        r.treatmentReasonName ?? (r.treatmentReasonCode ? `код ${r.treatmentReasonCode}` : null),
      placeOfService: r.placeOfService ?? null,
      deptType: r.deptType ?? null,
      ruleCode: r.ruleCode ?? null,
    };
  });

  return safe.map(toViewRow);
}

function Dashboard({ auth, role, roleLoading }: { auth: AuthPayload; role: Role | null; roleLoading: boolean }) {

  // Фильтры
  const [filters, setFilters] = useState<Filters>({ quick: "сегодня" });
  const isHospitalMode = filters.context === "Стационар";

  const debService = useDebounced(filters.serviceName ?? "", 200);
  const qService = useDeferredValue(debService.toLowerCase());

  // Роль пользователя (SELF / DEPARTMENT / ORG / DIRECTOR / SUPERADMIN)
  /*const { role, loading: roleLoading } = useRole(auth);*/

  // Строки таблицы
  const [rows, setRows] = useState<Row[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsErr, setRowsErr] = useState<string | null>(null);

  // Справочники (словарь организаций / отделений / врачей)
  const [dicts, setDicts] = useState<{
    orgs: OrgDictItem[];
    departments: DepartmentDictItem[];
    doctors: DoctorDictItem[];
    treatmentReasons: TreatmentReasonDictItem[];
  }>({ orgs: [], departments: [], doctors: [], treatmentReasons: [] });
  const [dictsLoading, setDictsLoading] = useState(false);
  const [dictsErr, setDictsErr] = useState<string | null>(null);

  // Строки для отображения.
  //
  // Повторной фильтрации на фронте здесь БОЛЬШЕ НЕТ, и это принципиально:
  // сервер отбирает по тем же полям, но страницу обрезает ДО того, как фронт
  // успевал что-то выбросить. Из-за этого по фильтру «Поликлиника» счётчик
  // показывал 40 дефектов, а в таблице оставалось 2 — фронт выбрасывал строки
  // из уже обрезанной страницы. Отбор делает только сервер, ровно один раз.
  const rowsForView = rows;

  // --- Постраничность ---
  // Сервер отдаёт страницу и признак «есть ещё». Раньше выдача молча обрезалась
  // на 500 строке (за месяц дефектов 1 677, за год — 361 132), и понять,
  // что список неполный, было неоткуда.
  const PAGE_SIZE = 500;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Смена любого фильтра возвращает на первую страницу — иначе можно остаться
  // на пятой странице выборки, в которой всего одна.
  useEffect(() => {
    setPage(1);
  }, [filters.context, filters.department, filters.doctor, filters.orgId, filters.deptType, filters.treatmentReason, qService, filters.from, filters.to, filters.quick]);

  // Параметры запроса к аналитике — общие для таблицы и для выгрузки.
  const buildAnalyticsParams = useMemo(() => {
    return () => {
      const [startTs, endTs] = resolveAnalyticsWindow(filters);
      const params = new URLSearchParams();
      params.set("from", tsToIsoUTC(startTs));
      params.set("to", tsToIsoUTC(endTs));
      if (filters.context)    params.set("context", filters.context);
      if (filters.department) params.set("department", filters.department);
      if (filters.doctor)     params.set("doctor", filters.doctor);
      if (filters.orgId)      params.set("orgId", filters.orgId);
      if (filters.deptType)   params.set("deptType", filters.deptType);
      if (filters.treatmentReason) params.set("treatmentReason", filters.treatmentReason);
      if (qService)           params.set("serviceName", qService);
      params.set("okOnly", "true");
      params.set("sort", "arrivedAt");
      params.set("order", "desc");
      return params;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.context, filters.department, filters.doctor, filters.orgId, filters.deptType, filters.treatmentReason, filters.from, filters.to, filters.quick, qService]);

  // --- Загрузка таблицы /api/v1/analytics/table ---
  useEffect(() => {
    if (isHospitalMode) {
      setRows([]);
      setHasMore(false);
      setRowsErr(null);
      setRowsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadRows() {
      setRowsLoading(true);
      setRowsErr(null);
      try {
        const params = buildAnalyticsParams();
        params.set("page", String(page));
        params.set("page_size", String(PAGE_SIZE));

        const raw = await apiFetch(`/api/v1/analytics/table?${params.toString()}`, {}, auth);
        if (cancelled) return;

        setRows(mapServerRows(raw));
        setHasMore(!!(raw as any)?.hasMore);
      } catch (e: any) {
        if (!cancelled) setRowsErr(e.message || "Ошибка загрузки таблицы");
      } finally {
        if (!cancelled) setRowsLoading(false);
      }
    }

    if (auth?.access_token && auth?.iin) {
      loadRows();
    }

    // Из трёх запросов экрана защиты от устаревшего ответа не было только у таблицы:
    // при быстром переключении фильтров на экране оседал тот ответ, который вернулся
    // последним, а не тот, который соответствует текущему выбору.
    return () => {
      cancelled = true;
    };
  }, [auth, buildAnalyticsParams, page, isHospitalMode]);

  // --- Загрузка справочников /api/v1/analytics/dicts ---
  useEffect(() => {
    let cancelled = false;

    async function loadDicts() {
      setDictsLoading(true);
      setDictsErr(null);
      try {
        const [startTs, endTs] = resolveAnalyticsWindow(filters);

        const params = new URLSearchParams();
        params.set("from", tsToIsoUTC(startTs));
        params.set("to", tsToIsoUTC(endTs));
        if (!isHospitalMode) {
          if (filters.context)    params.set("context", filters.context);
          if (filters.orgId)      params.set("orgId", filters.orgId);
          if (filters.department) params.set("department", filters.department);
          if (filters.doctor)     params.set("doctor", filters.doctor);
          if (filters.deptType)   params.set("deptType", filters.deptType);
          if (qService)           params.set("serviceName", qService);
        }

        const data = await apiFetch(`/api/v1/analytics/dicts?${params.toString()}`, {}, auth);

        if (!cancelled) {
          setDicts({
            orgs: Array.isArray(data.orgs) ? data.orgs : [],
            departments: Array.isArray(data.departments) ? data.departments : [],
            doctors: Array.isArray(data.doctors) ? data.doctors : [],
            treatmentReasons: Array.isArray(data.treatmentReasons) ? data.treatmentReasons : [],
          });
        }
      } catch (e: any) {
        if (!cancelled) {
          setDictsErr(e.message || "Ошибка загрузки справочников");
        }
      } finally {
        if (!cancelled) {
          setDictsLoading(false);
        }
      }
    }

    if (auth?.access_token && auth?.iin) {
      loadDicts();
    }

    return () => {
      cancelled = true;
    };
  }, [
    auth,
    filters.context,
    filters.orgId,
    filters.department,
    filters.doctor,
    filters.deptType,
    filters.from,
    filters.to,
    filters.quick,
    qService,
    isHospitalMode,
  ]);

  // --- Загрузка счётчиков /api/v1/analytics/counters ---
  const [counters, setCounters] = useState<Counters | null>(null);
  const [countersLoading, setCountersLoading] = useState(false);
  const [countersErr, setCountersErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCounters() {
      if (isHospitalMode) {
        if (!cancelled) {
          setCounters(null);
          setCountersErr(null);
          setCountersLoading(false);
        }
        return;
      }
      setCountersLoading(true);
      setCountersErr(null);
      try {
        const [startTs, endTs] = resolveAnalyticsWindow(filters);

        const params = new URLSearchParams();
        params.set("from", tsToIsoUTC(startTs));
        params.set("to", tsToIsoUTC(endTs));
        if (filters.context)    params.set("context", filters.context);
        if (filters.department) params.set("department", filters.department);
        if (filters.doctor)     params.set("doctor", filters.doctor);
        if (filters.orgId)      params.set("orgId", filters.orgId);
        if (filters.deptType)   params.set("deptType", filters.deptType);
        if (filters.treatmentReason) params.set("treatmentReason", filters.treatmentReason);
        if (qService)           params.set("serviceName", qService);

        const data = await apiFetch(`/api/v1/analytics/counters?${params.toString()}`, {}, auth);

        if (!cancelled) {
          setCounters(data as Counters);
        }
      } catch (e: any) {
        if (!cancelled) {
          setCountersErr(e.message || "Ошибка загрузки счётчиков");
        }
      } finally {
        if (!cancelled) {
          setCountersLoading(false);
        }
      }
    }

    if (auth?.access_token && auth?.iin) {
      loadCounters();
    }

    return () => {
      cancelled = true;
    };
  }, [auth, filters.context, filters.department, filters.doctor, filters.orgId, filters.deptType, filters.treatmentReason, qService, filters.from, filters.to, filters.quick, isHospitalMode]);

  // --- Опции для селектов из dicts ---
  const orgOptions = useMemo(() => {
    return dicts.orgs
      .filter(o => (o.orgId ?? "").trim() !== "")
      .map(o => {
        const id = (o.orgId || "").trim();
        const name = (o.orgName || "").trim();
        return {
          id,
          label: name || id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "ru"));
  }, [dicts.orgs]);

  const departmentOptions = useMemo(() => {
    return dicts.departments
      .map(d => (d.department || "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "ru"));
  }, [dicts.departments]);

  const doctorOptions = useMemo(() => {
    return dicts.doctors
      .map(d => (d.doctor || "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "ru"));
  }, [dicts.doctors]);

  // Можно ли делать экспорт
  const canExport = (role === "DEPARTMENT" || role === "ORG" || role === "DIRECTOR" || role === "SUPERADMIN");

  if (isHospitalMode) {
    return (
      <HospitalDashboardContent
        role={role || "SELF"}
        roleLoading={roleLoading}
        filters={filters}
        setFilters={setFilters}
        orgOptions={orgOptions}
      />
    );
  }

  const rangeFrom = (page - 1) * PAGE_SIZE;
  const rangeTo = rangeFrom + rows.length;

  // --- Выгрузка CSV ---
  // Выгружаем ВСЕ страницы выборки, а не только ту, что на экране: раньше в файл
  // уходило ровно то, что успело загрузиться, и обрезанная выгрузка выглядела полной.
  const EXPORT_MAX_ROWS = 20000;

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all: Row[] = [];
      let p = 1;
      let more = true;

      while (more && all.length < EXPORT_MAX_ROWS) {
        const params = buildAnalyticsParams();
        params.set("page", String(p));
        params.set("page_size", String(PAGE_SIZE));
        const raw = await apiFetch(`/api/v1/analytics/table?${params.toString()}`, {}, auth);
        all.push(...mapServerRows(raw));
        more = !!(raw as any)?.hasMore;
        p += 1;
      }

      if (more) {
        alert(
          `Выгружено ${all.length.toLocaleString("ru-RU")} строк — это предел одной выгрузки. ` +
          `Чтобы получить остальное, сузьте период или фильтры.`
        );
      }

      exportToCsv(all, role || "SELF");
    } catch (e: any) {
      alert(e?.message || "Не удалось выгрузить данные");
    } finally {
      setExporting(false);
    }
  };

  // --- Открытие документа в МИС по клику по ID услуги / диагноз ---
  const handleOpenMisDoc = async (row: Row) => {
    if (!row.docId || !row.orgId) {
      return;
    }
    try {
      const params = new URLSearchParams({
        docId: row.docId,
        orgId: row.orgId,
      });
      const resp = await apiFetch(`/api/v1/analytics/mis-doc-url?${params.toString()}`, {}, auth);
      let url = "";
      if (resp && typeof resp === "object" && "url" in resp) {
        url = (resp as any).url;
      } else if (typeof resp === "string") {
        url = resp;
      }
      if (!url || typeof url !== "string") {
        alert("Не удалось получить ссылку из МИС");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      alert(e?.message || "Ошибка при обращении к МИС");
    }
  };

  return (
    <div style={css.container}>
      <div style={{ marginBottom: 12 }}>
        <CompactFilters
          filters={filters}
          setFilters={setFilters}
          role={role || "SELF"}
          canExport={canExport}
          onExport={handleExport}
          orgOptions={orgOptions}
          departmentOptions={departmentOptions}
          doctorOptions={doctorOptions}
          treatmentReasonOptions={dicts.treatmentReasons}
        />
        {(rowsLoading || countersLoading || roleLoading || dictsLoading) && (
          <div style={{ marginTop: 6, fontSize: 12 }}>Загрузка…</div>
        )}
        {rowsErr && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#ef4444" }}>{rowsErr}</div>
        )}
        {countersErr && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#ef4444" }}>{countersErr}</div>
        )}
        {dictsErr && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#ef4444" }}>{dictsErr}</div>
        )}
      </div>

      <div style={css.gridMain}>
        <div style={{ overflow: "hidden" }}>
          <LeftPanel rows={rows} counters={counters} loading={countersLoading} />
        </div>
        <div style={{ display: "grid", gap: 6, minWidth: 0, alignContent: "start" }}>
          <DataGrid
            data={rowsForView}
            role={role || "SELF"}
            onOpenMisDoc={handleOpenMisDoc}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#475569", minHeight: 22 }}>
            <span>
              {rowsLoading
                ? "Загрузка…"
                : rows.length === 0
                ? "Дефектов по выбранным фильтрам нет"
                : hasMore || page > 1
                ? `Строки ${(rangeFrom + 1).toLocaleString("ru-RU")}–${rangeTo.toLocaleString("ru-RU")}${hasMore ? "" : " (последняя страница)"}`
                : `Показано ${rows.length.toLocaleString("ru-RU")} — это всё`}
            </span>

            {(hasMore || page > 1) && (
              <span style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  style={pagerBtn}
                  disabled={page <= 1 || rowsLoading}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  ← назад
                </button>
                <button
                  type="button"
                  style={pagerBtn}
                  disabled={!hasMore || rowsLoading}
                  onClick={() => setPage(p => p + 1)}
                >
                  вперёд →
                </button>
              </span>
            )}

            {exporting && <span>Готовим выгрузку…</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function HospitalDashboardContent({
  role,
  roleLoading,
  filters,
  setFilters,
  orgOptions,
}: {
  role: Role;
  roleLoading: boolean;
  filters: Filters;
  setFilters: (f: Filters) => void;
  orgOptions: { id: string; label: string }[];
}) {
  const hospitalWindow = useMemo(() => resolveHospitalWindow(filters), [filters.from, filters.to, filters.quick]);
  const hospitalQuery = useMemo(
    () => ({
      mis_id: filters.orgId ?? null,
      department: filters.department ?? null,
      doctor: filters.doctor ?? null,
      from_date: hospitalWindow.from_date,
      to_date: hospitalWindow.to_date,
      limit: 1000,
      offset: 0,
    }),
    [filters.orgId, filters.department, filters.doctor, filters.context, hospitalWindow.from_date, hospitalWindow.to_date],
  );

  const { rows, total, loading, error } = useHospitalResults(hospitalQuery);
  const {
    fixedTransitions,
    loading: countersLoading,
    error: countersError,
  } = useHospitalCounters(hospitalQuery);

  const departmentOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((row) => {
      const value = (row.department || "").trim();
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
  }, [rows]);

  const doctorOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((row) => {
      const value = (row.doctor || "").trim();
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
  }, [rows]);

  const effectiveRows = rows;

  return (
    <div style={css.container}>
      <div style={{ marginBottom: 12 }}>
        <CompactFilters
          filters={filters}
          setFilters={setFilters}
          role={role}
          canExport={role !== "SELF" && role !== "DEPARTMENT"}
          orgOptions={orgOptions}
          departmentOptions={departmentOptions}
          doctorOptions={doctorOptions}
          treatmentReasonOptions={[]}
          onExport={() => exportHospitalToCsv(effectiveRows)}
        />
        {(loading || countersLoading || roleLoading) && <div style={{ marginTop: 6, fontSize: 12 }}>Загрузка…</div>}
        {error && <div style={{ marginTop: 6, fontSize: 12, color: "#ef4444" }}>{error}</div>}
        {countersError && <div style={{ marginTop: 6, fontSize: 12, color: "#ef4444" }}>{countersError}</div>}
        {!loading && !error && total === 0 && <div style={{ marginTop: 6, fontSize: 12 }}>Ошибок по hospital не найдено</div>}
      </div>

      <div style={css.gridMain}>
        <div style={{ overflow: "hidden" }}>
          <HospitalLeftPanel rows={effectiveRows} total={total} fixedTransitions={fixedTransitions} />
        </div>
        <HospitalResultsTable rows={effectiveRows} />
      </div>
    </div>
  );
}

/* ========================= Login ========================= */

function Login({ onLogin }: { onLogin: (login: string, password: string) => Promise<void> }) {
  const [iin, setIin] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "#f7f8fa", zIndex: 999 }} />
      <div style={{
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 16,
        width: 380,
        maxWidth: "96vw",
        display: "grid",
        gap: 10,
        textAlign: "center",
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
      }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Вход в систему</div>
        <input placeholder="ИИН" value={iin} onChange={(e) => setIin(e.target.value)} style={css.input as any} />
        <input placeholder="Пароль" type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={css.input as any} />
        {err && <div style={{ color: "#ef4444", fontSize: 12 }}>{err}</div>}
        <button
          style={{ ...css.button, width: "100%", background: ACCENT, color: "#fff", borderColor: ACCENT, fontWeight: 700 } as any}
          disabled={loading}
          onClick={async () => {
            try {
              setErr(null); setLoading(true);
              await onLogin(iin.trim(), password);
            } catch (e: any) {
              setErr(e.message || "Ошибка входа");
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? "Входим…" : "Войти"}
        </button>
      </div>
    </>
  );
}

/* ========================= App ========================= */

export default function App() {
  const { user, auth, loginBackend, logout } = useAuth();
  const { role, loading: roleLoading } = useRole(auth);
  const [showTopMenu, setShowTopMenu] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadTopMenuAccess() {
      if (role === "SUPERADMIN") {
        if (!cancelled) setShowTopMenu(true);
        return;
      }
      if (role !== "DIRECTOR" || !auth?.access_token || !auth?.iin) {
        if (!cancelled) setShowTopMenu(false);
        return;
      }

      try {
        const data = await apiFetch("/api/v1/analytics/dicts", {}, auth);
        const orgs = Array.isArray((data as any)?.orgs) ? (data as any).orgs : [];
        const canSee = orgs.some((org: any) => {
          const label = String(org?.orgName || "").trim().toLowerCase();
          return label.includes("караса");
        });
        if (!cancelled) setShowTopMenu(canSee);
      } catch {
        if (!cancelled) setShowTopMenu(false);
      }
    }

    void loadTopMenuAccess();
    return () => {
      cancelled = true;
    };
  }, [auth?.access_token, auth?.iin, role]);

  if (!user || !auth) return <Login onLogin={loginBackend} />;

  return (
    <div style={css.page}>
      <TopBar user={user} showMenu={showTopMenu} onLogout={logout} />
      <Dashboard auth={auth} role={role} roleLoading={roleLoading} />
    </div>
  );
}

function HospitalLeftPanel({ rows, total, fixedTransitions }: { rows: HospitalResultRow[]; total: number; fixedTransitions: number }) {
  const caseCount = total || rows.length;
  const defectCount = rows.reduce((acc, row) => acc + (Number(row.defect_count) || 0), 0);
  const doctorsWithErrors = new Set(rows.map((row) => (row.doctor || "").trim()).filter(Boolean)).size;

  const Card = ({ title, value }: { title: string; value: string }) => (
    <div style={css.card}>
      <div style={css.cardTitle}>{title}</div>
      <div style={css.bigNum}>{value}</div>
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 8, overflow: "hidden" }}>
      <Card title="Случаев с ошибками" value={caseCount.toLocaleString("ru-RU")} />
      <Card title="Ошибок всего" value={defectCount.toLocaleString("ru-RU")} />
      <Card title="Врачей с ошибками" value={doctorsWithErrors.toLocaleString("ru-RU")} />
      <Card title="Исправленные ошибки" value={fixedTransitions.toLocaleString("ru-RU")} />
    </div>
  );
}

